import type { AvailabilityDisplayType, CalendarDayType, MergedCalendarDay } from '../types/calendar';
import type { ServiceType } from '../state/types';
import { CALENDAR_DAY_TYPE_REQUEST } from '../types/calendar';

export const CLICKABLE_AVAILABILITY_TYPES: AvailabilityDisplayType[] = [
  'available', 'request', 'waiting-list', 'walk-in',
];

export function isDisplayTypeClickable(displayType?: AvailabilityDisplayType): boolean {
  if (!displayType) return true;
  return CLICKABLE_AVAILABILITY_TYPES.includes(displayType);
}

export function getIndicatorColor(displayType?: AvailabilityDisplayType): string {
  if (!displayType) return 'bg-foreground/30';
  switch (displayType) {
    case 'available':
    case 'request':
      return 'bg-green-400';
    case 'waiting-list':
      return 'bg-yellow-400';
    case 'walk-in':
      return 'bg-blue-400';
    case 'not-available':
    case 'closed':
      return 'bg-foreground/30';
    default:
      return 'bg-foreground/30';
  }
}

/**
 * Map CalendarDayType to AvailabilityDisplayType for styling
 */
export function mapToDisplayType(type: CalendarDayType): AvailabilityDisplayType {
  switch (type) {
    case 'available':
      return 'available';
    case 'request':
      return 'request';
    case 'waiting-list':
      return 'waiting-list';
    case 'walk-in':
      return 'walk-in';
    case 'not-available':
    case 'not-available-old-day':
      return 'not-available';
    case 'not-available-closed':
      return 'closed';
    default:
      return 'not-available';
  }
}

/**
 * Override calendar days for intent capture (first visit).
 * Shows all non-closed days as "available" so the user expresses
 * their real date preference without being discouraged by grayed-out days.
 * Preserves the original `type` in the data (early access detection reads it later)
 * — only overrides `displayType` for styling.
 *
 * Replicates legacy mergeCalendars() behavior (non-MeitreNetworkFlow):
 * any day that is not "not-available-closed" is shown as available.
 */
export function applyCalendarIntentOverride(days: MergedCalendarDay[]): MergedCalendarDay[] {
  return days.map((day) => {
    if (day.type === 'not-available-closed') {
      return day;
    }
    return { ...day, displayType: 'available' as AvailabilityDisplayType };
  });
}

/**
 * Build the booking state update for a selected date.
 * Shared between SelectDatePage and FullCalendarPage to avoid logic duplication.
 */
export function buildDateSelectionUpdate(
  dayAvail: MergedCalendarDay | undefined,
  restaurantServiceTypes: ServiceType[],
): {
  updates: Record<string, unknown>;
  availableServices: ServiceType[];
  singleService: ServiceType | null;
} {
  const availableServices = (dayAvail?.availableServiceTypes ?? []) as ServiceType[];

  const singleService: ServiceType | null = restaurantServiceTypes.length === 1
    ? restaurantServiceTypes[0]
    : availableServices.length === 1
      ? availableServices[0]
      : null;

  const updates: Record<string, unknown> = {
    calendarIntentCaptured: true,
    isEarlyAccessFlow: dayAvail?.type === CALENDAR_DAY_TYPE_REQUEST,
    isSelectedDateSpecial: dayAvail?.isSpecialDay ?? false,
    selectedCalendarDayType: dayAvail?.type ?? null,
    availableServiceTypesForDate: availableServices,
  };

  return { updates, availableServices, singleService };
}

/**
 * Decide whether the current `booking.service` should be cleared when the
 * user picks a new date.
 *
 * Why: a previous date may have left an auto-selected service in state
 * (e.g. single-available-service skip, or a special-event lock). On the
 * new date that service may no longer apply, in which case the user has
 * to re-pick at SERVICE step. But when an event restricts the booking to
 * exactly one service, that service is the lock — clearing it here strands
 * the flow because SERVICE step is skipped (see flow/steps.ts).
 */
export function buildServiceResetForDateSelection({
  currentService,
  availableServiceTypes,
  eventServiceTypes,
}: {
  currentService: ServiceType | null | undefined;
  availableServiceTypes: ServiceType[];
  eventServiceTypes?: ServiceType[] | null;
}): { service?: null } {
  if (!currentService) return {};
  if (availableServiceTypes.length === 1) return {};

  const eventLockedService =
    eventServiceTypes?.length === 1 ? eventServiceTypes[0] : null;
  if (eventLockedService && currentService === eventLockedService) return {};

  return { service: null };
}
